/**
 * Unlock screen — M5 version with passphrase + passkey paths.
 *
 * Primary UI:
 *  - If a passkey is enrolled: "Unlock with Face ID / Touch ID" primary button +
 *    "Use passphrase instead" secondary link.
 *  - Otherwise: passphrase form only.
 *
 * On passkey unlock failure (cancel, no PRF, unwrap error), falls back to
 * the passphrase form silently.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { Button } from '../ui/Button.js';
import { hasEnrolledPasskey, unlockWithPasskey } from './passkey.js';
import { weakPassphraseCheck } from '@pf/crypto-shared';

interface UnlockScreenProps {
  userId: string;
  onPassphraseUnlock: (passphrase: string) => void | Promise<void>;
  onPasskeyUnlock: (key: Uint8Array) => void | Promise<void>;
  error?: string | undefined;
  loading?: boolean | undefined;
}

export function UnlockScreen({
  userId,
  onPassphraseUnlock,
  onPasskeyUnlock,
  error,
  loading = false,
}: UnlockScreenProps) {
  const [passphrase, setPassphrase] = useState('');
  const [passphraseWarning, setPassphraseWarning] = useState<string | undefined>(undefined);
  const [showPassphraseForm, setShowPassphraseForm] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyAttempted, setPasskeyAttempted] = useState(false);

  useEffect(() => {
    void hasEnrolledPasskey(userId).then((enrolled) => {
      setHasPasskey(enrolled);
      // If no passkey, show the passphrase form immediately
      if (!enrolled) setShowPassphraseForm(true);
    });
  }, [userId]);

  function handlePassphraseChange(value: string) {
    setPassphrase(value);
    if (value.length > 0) {
      const check = weakPassphraseCheck(value);
      // Only show soft warnings (ok: false but score ≥ 40) while typing
      // Hard blocks are silent here — deriveKey will throw on submit
      if (!check.ok && check.score >= 40) {
        setPassphraseWarning(check.reason);
      } else {
        setPassphraseWarning(undefined);
      }
    } else {
      setPassphraseWarning(undefined);
    }
  }

  function handlePassphraseSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!passphrase.trim() || loading) return;
    void onPassphraseUnlock(passphrase);
  }

  async function handlePasskeyClick() {
    setPasskeyLoading(true);
    setPasskeyAttempted(true);
    try {
      const result = await unlockWithPasskey(userId);
      if (result) {
        await onPasskeyUnlock(result.key);
      } else {
        // Passkey failed or cancelled — show the passphrase form
        setShowPassphraseForm(true);
      }
    } catch {
      setShowPassphraseForm(true);
    } finally {
      setPasskeyLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center px-6 py-12">
      {/* Brand mark */}
      <div className="mb-10 text-center">
        <div
          className="w-16 h-16 rounded-2xl bg-[var(--color-accent-subtle)] border border-[var(--color-accent)] flex items-center justify-center mx-auto mb-4"
          aria-hidden="true"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <rect x="5" y="11" width="14" height="10" rx="2" stroke="var(--color-accent)" strokeWidth="2"/>
            <path d="M8 11V7a4 4 0 018 0v4" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)] tracking-tight">
          PersonalFinance
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Your data, decrypted locally.
        </p>
      </div>

      {/* Passkey primary unlock */}
      {hasPasskey && !showPassphraseForm && (
        <div className="w-full max-w-sm space-y-4">
          {error && (
            <p
              role="alert"
              data-testid="decrypt-error"
              className="text-sm text-[var(--color-negative)] flex items-start gap-2"
            >
              <ErrorIcon />
              {error}
            </p>
          )}
          <Button
            type="button"
            onClick={() => void handlePasskeyClick()}
            disabled={passkeyLoading || loading}
            className="w-full flex items-center justify-center gap-2"
          >
            {passkeyLoading ? (
              <>
                <SpinIcon />
                Unlocking…
              </>
            ) : (
              <>
                <FaceIdIcon />
                Unlock with Face ID / Touch ID
              </>
            )}
          </Button>
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowPassphraseForm(true)}
              className="text-sm text-[var(--color-text-secondary)] underline min-h-[44px]"
            >
              Use passphrase instead
            </button>
          </div>
          {passkeyAttempted && !passkeyLoading && (
            <p className="text-xs text-[var(--color-text-disabled)] text-center">
              Passkey sign-in unavailable. Enter your passphrase to continue.
            </p>
          )}
        </div>
      )}

      {/* Passphrase form */}
      {showPassphraseForm && (
        <form
          onSubmit={handlePassphraseSubmit}
          className="w-full max-w-sm space-y-4"
          aria-label="Unlock your financial data"
        >
          <div>
            <label
              htmlFor="passphrase"
              className="block text-sm font-medium text-[var(--color-text-primary)] mb-1.5"
            >
              Passphrase
            </label>
            <input
              id="passphrase"
              type="password"
              value={passphrase}
              onChange={(e) => handlePassphraseChange(e.target.value)}
              autoComplete="current-password"
              disabled={loading}
              aria-describedby={error ? 'unlock-error' : passphraseWarning ? 'passphrase-warning' : undefined}
              placeholder="Enter your passphrase"
              className={[
                'w-full px-4 py-3 rounded-lg text-sm',
                'bg-[var(--color-surface)] border text-[var(--color-text-primary)]',
                'placeholder:text-[var(--color-text-disabled)]',
                'focus:outline-none focus:border-[var(--color-accent)]',
                'transition-colors duration-[var(--duration-fast)]',
                'min-h-[44px]',
                error
                  ? 'border-[var(--color-negative)]'
                  : 'border-[var(--color-border)]',
                loading ? 'opacity-60 cursor-not-allowed' : '',
              ].join(' ')}
            />
          </div>

          {passphraseWarning && !error && (
            <p
              id="passphrase-warning"
              role="status"
              className="text-sm text-[var(--color-warning,#b45309)] flex items-start gap-2"
            >
              <WarningIcon />
              {passphraseWarning}
            </p>
          )}

          {error && (
            <p
              id="unlock-error"
              role="alert"
              data-testid="decrypt-error"
              className="text-sm text-[var(--color-negative)] flex items-start gap-2"
            >
              <ErrorIcon />
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || !passphrase.trim()}
            className="w-full"
          >
            {loading ? (
              <>
                <SpinIcon />
                Unlocking…
              </>
            ) : (
              'Unlock'
            )}
          </Button>

          {hasPasskey && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowPassphraseForm(false)}
                className="text-sm text-[var(--color-text-secondary)] underline min-h-[44px]"
              >
                Use Face ID / Touch ID instead
              </button>
            </div>
          )}
        </form>
      )}

      {/* Privacy note */}
      <p className="text-xs text-[var(--color-text-disabled)] mt-8 text-center max-w-xs">
        Your passphrase never leaves this device. Decryption happens locally in your browser.
      </p>
    </div>
  );
}

// ─── Icon helpers ─────────────────────────────────────────────────

function SpinIcon() {
  return (
    <svg
      className="animate-spin"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeDasharray="20 4" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="mt-0.5 flex-shrink-0">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="8" cy="10.5" r="0.75" fill="currentColor"/>
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="mt-0.5 flex-shrink-0">
      <path d="M8 2L14 13H2L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M8 6v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="8" cy="10.5" r="0.75" fill="currentColor"/>
    </svg>
  );
}

function FaceIdIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="17" y="2" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="2" y="17" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="17" y="17" width="5" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.8"/>
      <circle cx="9" cy="9" r="1.2" fill="currentColor"/>
      <circle cx="15" cy="9" r="1.2" fill="currentColor"/>
      <path d="M9 15c0.8 1.2 5.2 1.2 6 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
